import {
  Activity,
  Building2,
  CalendarCheck,
  CalendarDays,
  Sparkles,
  Sun,
  UserCheck,
  Users,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
} from '@/components/ui';
import { formatNumber } from '@/lib/utils';

import { useOverview } from './admin-api';
import type { RangeDays } from './admin-schemas';
import { SubscriptionsOverview } from './billing-overview';
import { BarList, Kpi, TimeBars } from './charts';
import { formatDay, langLabel, tradeLabel } from './labels';

export function OverviewSection({ days }: { days: RangeDays }) {
  const { t } = useTranslation('admin');
  const q = useOverview(days);
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  const loading = q.isLoading;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          icon={Building2}
          tone="accent"
          label={t('kpi.businesses')}
          value={d?.businesses.total}
          loading={loading}
        />
        <Kpi
          icon={CalendarCheck}
          tone="success"
          label={t('kpi.active7')}
          value={d?.businesses.active7}
          loading={loading}
        />
        <Kpi
          icon={CalendarDays}
          tone="teal"
          label={t('kpi.active30')}
          value={d?.businesses.active30}
          loading={loading}
        />
        <Kpi
          icon={Sparkles}
          tone="orange"
          label={t('kpi.newInRange')}
          value={d?.businesses.newInRange}
          loading={loading}
        />
        <Kpi
          icon={Users}
          tone="violet"
          label={t('kpi.users')}
          value={d?.users.total}
          loading={loading}
        />
        <Kpi
          icon={Sun}
          tone="warning"
          label={t('kpi.dau')}
          value={d?.users.dau}
          loading={loading}
        />
        <Kpi
          icon={UserCheck}
          tone="info"
          label={t('kpi.wau')}
          value={d?.users.wau}
          loading={loading}
        />
        <Kpi
          icon={Activity}
          tone="pink"
          label={t('kpi.mau')}
          value={d?.users.mau}
          loading={loading}
        />
      </div>

      <SubscriptionsOverview />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-3">
          <CardHeader tone="accent">
            <CardTitle>{t('overview.signups')}</CardTitle>
            <CardDescription>{t('overview.signupsHint')}</CardDescription>
          </CardHeader>
          <CardContent>
            {d ? (
              <TimeBars
                label={t('overview.signups')}
                points={d.signupsPerWeek.map((w) => ({
                  key: w.week,
                  value: w.businesses,
                  title: formatDay(w.week),
                }))}
                describe={(p) =>
                  t('overview.weekOf', { date: formatDay(p.key), count: formatNumber(p.value) })
                }
              />
            ) : null}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader tone="violet">
            <CardTitle>{t('overview.byTrade')}</CardTitle>
          </CardHeader>
          <CardContent>
            <BarList
              tone="violet"
              items={(d?.byTrade ?? []).map((x) => ({
                key: x.trade,
                label: tradeLabel(x.trade),
                value: x.businesses,
              }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader tone="teal">
            <CardTitle>{t('overview.byLanguage')}</CardTitle>
          </CardHeader>
          <CardContent>
            <BarList
              tone="teal"
              items={(d?.byLanguage ?? []).map((x) => ({
                key: x.locale,
                label: langLabel(x.locale),
                value: x.businesses,
              }))}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
